/**
 * IDF Validator
 *
 * Validates IDF documents against the IDD schema.
 * Provides errors and warnings for:
 * - Unknown object classes
 * - Missing required fields
 * - Invalid field types (number vs string)
 * - Out-of-range numeric values
 * - Invalid choice values
 * - Missing required objects
 * - Duplicate unique objects
 */

import type { IDFDocument, IDFObject, ParseError } from './types';
import type { IDDSchema, IDDObjectType, IDDField } from '../idd/types';

export interface ValidationResult {
  errors: ParseError[];
  warnings: ParseError[];
}

/**
 * Helper to get line number from an IDF object
 */
function getObjectLine(obj: IDFObject): number {
  return obj.sourceLocation?.startLine ?? 1;
}

/**
 * Validate an IDF document against an IDD schema
 */
export function validateDocument(
  document: IDFDocument,
  schema: IDDSchema
): ValidationResult {
  const errors: ParseError[] = [];
  const warnings: ParseError[] = [];

  // Track seen unique objects and object names
  const seenUniqueObjects = new Map<string, number>(); // className -> line number
  const seenObjectNames = new Map<string, Map<string, number>>(); // className -> (name -> line)

  // Validate each object
  for (const obj of document.objects) {
    const objectType = schema.objectTypes.get(obj.className.toLowerCase());
    const line = getObjectLine(obj);

    if (!objectType) {
      // Unknown object class
      errors.push({
        message: `Unknown object class: ${obj.className}`,
        line,
        column: 1,
        severity: 'error',
      });
      continue;
    }

    // Check for duplicate unique objects
    if (objectType.isUnique) {
      const existingLine = seenUniqueObjects.get(obj.className.toLowerCase());
      if (existingLine !== undefined) {
        errors.push({
          message: `Duplicate unique object '${obj.className}'. First instance at line ${String(existingLine)}`,
          line,
          column: 1,
          severity: 'error',
        });
      } else {
        seenUniqueObjects.set(obj.className.toLowerCase(), line);
      }
    }

    // Validate fields
    validateFields(obj, objectType, errors, warnings, seenObjectNames);
  }

  // Check for missing required objects
  for (const [, objectType] of schema.objectTypes) {
    if (objectType.isRequired && !seenUniqueObjects.has(objectType.name.toLowerCase())) {
      // Check if any object of this type exists
      const hasObject = document.objects.some(
        (obj) => obj.className.toLowerCase() === objectType.name.toLowerCase()
      );
      if (!hasObject) {
        warnings.push({
          message: `Missing required object: ${objectType.name}`,
          line: 1,
          column: 1,
          severity: 'warning',
        });
      }
    }
  }

  return { errors, warnings };
}

/**
 * Validate fields of an object
 */
function validateFields(
  obj: IDFObject,
  objectType: IDDObjectType,
  errors: ParseError[],
  warnings: ParseError[],
  seenObjectNames: Map<string, Map<string, number>>
): void {
  const line = getObjectLine(obj);

  // Check minimum fields
  if (obj.fields.length < objectType.minFields) {
    warnings.push({
      message: `Object '${obj.className}' has ${String(obj.fields.length)} fields, but requires at least ${String(objectType.minFields)}`,
      line,
      column: 1,
      severity: 'warning',
    });
  }

  // Validate each field
  for (let i = 0; i < obj.fields.length; i++) {
    const field = obj.fields[i];
    if (!field) continue;
    const fieldDef = objectType.fields[i];

    // Skip if no field definition (extensible objects may have extra fields)
    if (!fieldDef) {
      // Check if object is extensible
      if (objectType.extensible === 0) {
        warnings.push({
          message: `Object '${obj.className}' has extra field at position ${String(i + 1)}`,
          line,
          column: 1,
          severity: 'warning',
        });
      }
      continue;
    }

    // Get value
    const value = field.value.trim();

    // Check required field
    if (fieldDef.required && value === '') {
      errors.push({
        message: `Missing required field '${fieldDef.name || fieldDef.id}' in ${obj.className}`,
        line,
        column: 1,
        severity: 'error',
      });
      continue;
    }

    // Skip validation for empty non-required fields
    if (value === '') continue;

    // Check for autosize/autocalculate
    if (value.toLowerCase() === 'autosize' && !fieldDef.autosizable) {
      errors.push({
        message: `Field '${fieldDef.name || fieldDef.id}' does not support autosize`,
        line,
        column: 1,
        severity: 'error',
      });
      continue;
    }

    if (value.toLowerCase() === 'autocalculate' && !fieldDef.autocalculatable) {
      errors.push({
        message: `Field '${fieldDef.name || fieldDef.id}' does not support autocalculate`,
        line,
        column: 1,
        severity: 'error',
      });
      continue;
    }

    // Skip further validation for autosize/autocalculate
    if (
      value.toLowerCase() === 'autosize' ||
      value.toLowerCase() === 'autocalculate'
    ) {
      continue;
    }

    // Type-specific validation
    switch (fieldDef.type) {
      case 'real':
        validateRealField(value, fieldDef, obj.className, line, errors);
        break;
      case 'integer':
        validateIntegerField(value, fieldDef, obj.className, line, errors);
        break;
      case 'choice':
        validateChoiceField(value, fieldDef, obj.className, line, errors);
        break;
      case 'object-list':
        // Object-list validation would require full document analysis
        // For now, just check that it's not empty
        break;
    }

    // Track object names for reference validation (usually first field)
    if (i === 0 && fieldDef.references && fieldDef.references.length > 0) {
      if (!seenObjectNames.has(obj.className.toLowerCase())) {
        seenObjectNames.set(obj.className.toLowerCase(), new Map());
      }
      const nameMap = seenObjectNames.get(obj.className.toLowerCase());
      if (!nameMap) continue;

      // Check for duplicate names
      const existingLine = nameMap.get(value.toLowerCase());
      if (existingLine !== undefined) {
        errors.push({
          message: `Duplicate object name '${value}' in ${obj.className}. First instance at line ${String(existingLine)}`,
          line,
          column: 1,
          severity: 'error',
        });
      } else {
        nameMap.set(value.toLowerCase(), line);
      }
    }
  }
}

/**
 * Validate a real (floating-point) field value
 */
function validateRealField(
  value: string,
  fieldDef: IDDField,
  className: string,
  line: number,
  errors: ParseError[]
): void {
  const num = parseFloat(value);

  if (isNaN(num)) {
    errors.push({
      message: `Field '${fieldDef.name || fieldDef.id}' in ${className} expects a number, got '${value}'`,
      line,
      column: 1,
      severity: 'error',
    });
    return;
  }

  // Check minimum
  if (fieldDef.minimum !== undefined) {
    if (fieldDef.exclusiveMinimum ? num <= fieldDef.minimum : num < fieldDef.minimum) {
      errors.push({
        message: `Field '${fieldDef.name || fieldDef.id}' value ${String(num)} is below minimum ${fieldDef.exclusiveMinimum ? '>' : '>='} ${String(fieldDef.minimum)}`,
        line,
        column: 1,
        severity: 'error',
      });
    }
  }

  // Check maximum
  if (fieldDef.maximum !== undefined) {
    if (fieldDef.exclusiveMaximum ? num >= fieldDef.maximum : num > fieldDef.maximum) {
      errors.push({
        message: `Field '${fieldDef.name || fieldDef.id}' value ${String(num)} exceeds maximum ${fieldDef.exclusiveMaximum ? '<' : '<='} ${String(fieldDef.maximum)}`,
        line,
        column: 1,
        severity: 'error',
      });
    }
  }
}

/**
 * Validate an integer field value
 */
function validateIntegerField(
  value: string,
  fieldDef: IDDField,
  className: string,
  line: number,
  errors: ParseError[]
): void {
  const num = parseInt(value, 10);

  if (isNaN(num) || !Number.isInteger(parseFloat(value))) {
    errors.push({
      message: `Field '${fieldDef.name || fieldDef.id}' in ${className} expects an integer, got '${value}'`,
      line,
      column: 1,
      severity: 'error',
    });
    return;
  }

  // Check minimum
  if (fieldDef.minimum !== undefined) {
    if (fieldDef.exclusiveMinimum ? num <= fieldDef.minimum : num < fieldDef.minimum) {
      errors.push({
        message: `Field '${fieldDef.name || fieldDef.id}' value ${String(num)} is below minimum ${fieldDef.exclusiveMinimum ? '>' : '>='} ${String(fieldDef.minimum)}`,
        line,
        column: 1,
        severity: 'error',
      });
    }
  }

  // Check maximum
  if (fieldDef.maximum !== undefined) {
    if (fieldDef.exclusiveMaximum ? num >= fieldDef.maximum : num > fieldDef.maximum) {
      errors.push({
        message: `Field '${fieldDef.name || fieldDef.id}' value ${String(num)} exceeds maximum ${fieldDef.exclusiveMaximum ? '<' : '<='} ${String(fieldDef.maximum)}`,
        line,
        column: 1,
        severity: 'error',
      });
    }
  }
}

/**
 * Validate a choice field value
 */
function validateChoiceField(
  value: string,
  fieldDef: IDDField,
  className: string,
  line: number,
  errors: ParseError[]
): void {
  if (!fieldDef.choices || fieldDef.choices.length === 0) {
    return;
  }

  const valueLower = value.toLowerCase();
  const isValidChoice = fieldDef.choices.some(
    (choice) => choice.toLowerCase() === valueLower
  );

  if (!isValidChoice) {
    const validChoices = fieldDef.choices.join(', ');
    errors.push({
      message: `Invalid value '${value}' for field '${fieldDef.name || fieldDef.id}' in ${className}. Valid choices: ${validChoices}`,
      line,
      column: 1,
      severity: 'error',
    });
  }
}

/**
 * Create a simple validation function that can be used with the IDF store
 */
export function createValidator(
  schema: IDDSchema | null
): (document: IDFDocument) => ParseError[] {
  return (document: IDFDocument): ParseError[] => {
    if (!schema) {
      return [];
    }

    const result = validateDocument(document, schema);
    return [...result.errors, ...result.warnings];
  };
}
